import { addQueryStringParameter, getCleanID, GetPiwikSession, getQueryStringParameter } from 'lib/utils';
import { ParsedUrlQuery } from 'querystring';
import { County } from 'models/plans-search-types';
import { getUTCdate } from 'lib/utils';
import { PlanYear } from './Types/PlanYear.Type';
import { DrugCoverageTableGroup, UsedOption } from './Types/DrugCoverageTables.Type';
import { UrlFields } from './Types/ApplyToEnroll';
import { EvolveUrl } from 'lib/enum';

/**
 *Builds out the URL with all query strings attached
 *
 * @param {string} url The base url the start with
 * @param {ParsedUrlQuery} query An existing query string from router.query
 * @param {[string,string][]} newQueries An array of sets of strings used to create new querystrings [name, value]
 * @returns {string} new url
 *
 */
export function buildURL(
  url: string,
  query: ParsedUrlQuery | null,
  newQueries: [string, string][] = [],
  currentLanguage = ''
): string {
  url = url + '?';

  newQueries.forEach((queryItem) => {
    url = url + (url.endsWith('?') ? '' : '&') + queryItem[0] + '=' + queryItem[1];
  });

  const defaultLanguage = 'en';

  if (currentLanguage && currentLanguage !== defaultLanguage && !url.startsWith(`/${currentLanguage}/`)) {
    url = `/${currentLanguage}${url}`;
  }

  // If the URL already has default language in it, we need to remove it
  if (url.startsWith(`/${defaultLanguage}/`)) {
    url = url.replace(`/${defaultLanguage}/`, '/');
  }

  if (query === null) {
    return url;
  }

  const state = getQueryStringParameter('state', query);
  const countyName = getQueryStringParameter('county', query);
  const year = getQueryStringParameter('year', query);
  const zipcode = getQueryStringParameter('zipcode', query);
  const member = getQueryStringParameter('ExistingMember', query);
  if (state !== '') {
    url = url + '&state=' + state;
  }
  if (countyName !== '') {
    url = url + '&county=' + countyName;
  }
  if (year !== '') {
    url = url + '&year=' + year;
  }
  if (zipcode !== '') {
    url = url + '&zipcode=' + zipcode;
  }
  if (member !== '') {
    url = url + '&ExistingMember=' + member;
  }

  return url;
}

export async function buildApplyToEnrollURL(fields: UrlFields) {
  if (fields.crmPrefix == '') {
    return '#prefix';
  } else {
    return buildEvolveURL(fields.crmPrefix, fields.mcProductID, fields.zipcode, fields.county, fields.year, fields.cta);
  }
}

/**
 * builds evolve URL
 * @param {boolean} isPreview if true, use the sandbox URL
 * @param {string} token the daily generated token retrieved from the API /sc-api/custom/evolvetokengenerator/gettoken
 * @param {string | undefined} piwikSession piwik session id
 * @param {string | undefined} mcproductid evolve plan ID
 * @param {string | undefined} zip zipcode of current user
 * @param {string | undefined} county county of current user
 * @param {string | undefined} year year of plans to search
 * @param {string | undefined} cta evolve tracking string
 * @returns entire URL ready to use
 */
export function buildEvolveURL(
  prefix: string,
  mcproductid: string | undefined,
  zip: string | undefined,
  county: string | undefined,
  year: string | undefined,
  cta: string
) {
  const piwikSession = GetPiwikSession();

  let url = `${EvolveUrl.EvolveHandler}`;
  url = addQueryStringParameter(url, 'prefix', prefix);
  url = addQueryStringParameter(url, 'pwksessionid', piwikSession as string);
  url = addQueryStringParameter(url, 'mcproductid', mcproductid as string);
  url = addQueryStringParameter(url, 'zip', zip as string);
  url = addQueryStringParameter(url, 'county', county as string);
  url = addQueryStringParameter(url, 'year', year as string);
  url = addQueryStringParameter(url, 'cta', cta as string);

  return url;
}

export const checkPlans = (county: County, year: string) => {
  let isInError = false;
  const keys = Object.keys(county.PlansByYear);

  if (keys.length == 0 || keys.indexOf(year) == -1) {
    isInError = true;
  }
  return isInError;
};

/**
 *
 * @param {YearItem[]} planYears PlanYears data puled from Global Data in Sitecore
 * @param {string} selectedYear The plan year selected by the user
 * @returns {boolean} Should enroll button be enabled?
 */
export function checkEnrollEnable(planYears: PlanYear[], selectedYear: number) {
  const matchingYear = planYears?.find((x) => x.year == selectedYear);
  const now = getUTCdate(new Date());
  const enrollButtonStartDate = getUTCdate(matchingYear?.enrollButtonStart ?? new Date(3000, 12, 31));
  return enrollButtonStartDate && enrollButtonStartDate <= now;
}

export function getMatchingDrugCoverageGroupOption(groups: DrugCoverageTableGroup[], matchID: string) {
  let newGroup: UsedOption = {
    drugCoverageTables: [],
    groupCaption: '',
    planComparisonTableHeader: '',
  };
  for (let x = 0; x < groups.length; x++) {
    const matchedOption = groups[x].options.find((opt) => getCleanID(opt.id) == matchID);
    if (matchedOption == undefined) {
      continue;
    } else {
      newGroup = {
        drugCoverageTables: matchedOption.drugCoverageTables,
        groupCaption: groups[x].groupCaption,
        planComparisonTableHeader: groups[x].planComparisonTableHeader,
      };
      break;
    }
  }
  return newGroup;
}
